import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Save, RotateCcw } from 'lucide-react';
import api, { formatApiError } from '../../api';
import { Button } from '../../components/ui/button';

export default function NDATemplateSettings() {
    const [body, setBody]   = useState('');
    const [original, setOriginal] = useState('');
    const [placeholders, setPlaceholders] = useState({});
    const [busy, setBusy]   = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/ndas/template');
            setBody(r.data.body_md);
            setOriginal(r.data.body_md);
            setPlaceholders(r.data.placeholders || {});
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to load template');
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setBusy(true);
        try {
            await api.put('/ndas/template', { body_md: body });
            toast.success('Template saved');
            setOriginal(body);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Save failed');
        } finally {
            setBusy(false);
        }
    };

    const insertPlaceholder = (key) => {
        setBody(b => `${b}{${key}}`);
    };

    if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

    const dirty = body !== original;

    return (
        <div className="space-y-4" data-testid="nda-template-settings">
            <div>
                <h2 className="text-[16px] font-semibold text-slate-900">NDA template</h2>
                <p className="text-[12.5px] text-slate-500 mt-1">
                    This is the master template used when you create a new NDA. Use placeholders
                    like <code>{'{recipient_name}'}</code> — they're filled per-NDA at render time.
                    Per-NDA edits don't affect this template.
                </p>
            </div>

            <div className="grid grid-cols-4 gap-4">
                <div className="col-span-3">
                    <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={28}
                        className="w-full font-mono text-[12px] rounded-md border border-slate-300 px-3 py-2"
                        data-testid="nda-template-body"
                    />
                </div>
                <div className="col-span-1">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">Placeholders</div>
                    <div className="text-[11px] text-slate-500 mb-2">Click to append at the cursor.</div>
                    <div className="space-y-1 max-h-[460px] overflow-y-auto">
                        {Object.entries(placeholders).map(([k, label]) => (
                            <button
                                key={k}
                                onClick={() => insertPlaceholder(k)}
                                className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-[12px] border border-transparent hover:border-slate-200"
                                data-testid={`placeholder-${k}`}
                            >
                                <code className="text-[11px] text-violet-700">{`{${k}}`}</code>
                                <div className="text-[10.5px] text-slate-500">{label}</div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <Button onClick={save} disabled={busy || !dirty} data-testid="nda-template-save">
                    <Save className="w-4 h-4 mr-1" /> {busy ? 'Saving…' : (dirty ? 'Save template' : 'Saved')}
                </Button>
                {dirty && (
                    <Button variant="outline" onClick={() => setBody(original)} data-testid="nda-template-reset">
                        <RotateCcw className="w-4 h-4 mr-1" /> Revert
                    </Button>
                )}
            </div>
        </div>
    );
}
